import React from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

import { KnzArticleHeader } from './KnzArticleHeader';
import { KnzDocumentProvider, TocMode } from './KnzDocumentContext';
import { KnzFileResolver, KnzFileResolverProvider } from './KnzFileResolverContext';
import { InlineText } from './embeds/InlineText';
import { deriveArticleNavIds } from './embeds/blocks/ArticleNav';
import { ArticleNavContextValue, ArticleNavSlotProvider } from './embeds/blocks/ArticleNavContext';
import { ProseArticle } from './embeds/blocks/prose/ProseArticle';
import { getComponentDef } from './embeds/registry';
import { registryLookup } from './embeds/registry';
import { parseKnz } from './parser';
import { KnzNode } from './types';

/** 目次項目（KnzViewer が本文の Section から収集して親へ通知する） */
export type KnzTocItem = {
  /** ジャンプ先の要素 id（Section の anchor） */
  anchor: string;
  /** 目次に表示するラベル（Section の title） */
  label: string;
  /** サブセクションかどうか（Section の subNumber が指定されている） */
  isSub: boolean;
};

/**
 * ルート配下を走査して Section（anchor + title を持つもの）を目次項目として収集する。
 * ネストした Section も拾う（Toc ブロックと同じ収集規則）。
 */
const collectTocItems = (nodes: KnzNode[]): KnzTocItem[] => {
  const items: KnzTocItem[] = [];
  const walk = (ns: KnzNode[]) => {
    for (const n of ns) {
      if (n.name === 'Section') {
        const anchor = n.params.anchor as string | undefined;
        const title = n.params.title as string | undefined;
        const subNumber = n.params.subNumber as string | undefined;
        if (anchor && title) {
          items.push({ anchor, label: title, isSub: subNumber != null && subNumber !== '' });
        }
      }
      walk(n.children);
    }
  };
  walk(nodes);
  return items;
};

/** ルート配下に Toc ブロックが存在するか */
const hasTocBlock = (nodes: KnzNode[]): boolean => {
  for (const n of nodes) {
    if (n.name === 'Toc') return true;
    if (hasTocBlock(n.children)) return true;
  }
  return false;
};

type Props = {
  /** knz ソース文字列 */
  source: string;
  /** ファイル作成日時（記事ヘッダー用） */
  createdAt?: string;
  /** ファイル更新日時（記事ヘッダー用） */
  updatedAt?: string;
  /**
   * frontmatter ヘッダー（KnzArticleHeader）の描画を抑制する。
   * 記事詳細画面では記事メタヘッダーを別途描画するため、二重表示を避ける用途で true にする（記事機能 D-37）。
   * prose モードはもともとヘッダーを描画しないため影響しない。
   */
  hideHeader?: boolean;
  /**
   * 本文中の外部ファイル参照（CodeBlock の link など）を解決する取得関数。
   * 記事詳細（ゲスト閲覧可）では公開用API（getPublicArticleFile / API6111）を渡す。
   * 未指定の場合、embed は認証必須のファイル詳細取得APIにフォールバックする。
   */
  fetchFile?: KnzFileResolver;
  /**
   * Toc（目次）ブロックの描画モード。
   * - inline（デフォルト）: 従来どおり本文中に目次を描画する
   * - hoist: 本文中には描画せず、収集した目次を onTocChange で親へ通知する（記事公開ページ用）
   * - ignore: 目次を描画しない（エディタ等のプレビュー用）
   */
  tocMode?: TocMode;
  /**
   * hoist モード時に、本文中の Toc ブロックの有無と収集した目次項目を親へ通知するコールバック。
   * items は Toc ブロックが無い場合は空配列。親はこれを見て右カラム目次を出すか判断する。
   */
  onTocChange?: (items: KnzTocItem[]) => void;
  /**
   * ArticleNav（記事ナビ）の遷移コンテキスト（仕様 Q-8）。
   * - 'preview'（既定・ワークスペースのプレビュー）: リンクは新規タブで /devdocs/article/:id を開く
   * - 'article'（記事詳細）: 通常のルーティング遷移
   */
  articleNavContext?: ArticleNavContextValue;
  /**
   * ArticleNav の有無と、下部スロットの実高さ（px）を親へ通知するコールバック。
   * 記事詳細（ArticleDetail）が末尾スペーサーから ArticleNav の領域ぶんを差し引くために使う。
   * - present: 本文に ArticleNav ブロックがあるか
   * - bottomHeight: 下部スロット（前後ナビ＋関連記事）の実高さ（px。未描画時は 0）
   * - displayedIds: ArticleNav が実際に表示する articleId（self + prev/next + 関連先頭 6 件）。
   *   おすすめ記事の重複除外に使う。
   * - overflowIds: 関連記事の 7 件目以降（表示されない分）。おすすめ記事の先頭に持っていくのに使う。
   */
  onArticleNavChange?: (info: {
    present: boolean;
    bottomHeight: number;
    displayedIds: string[];
    overflowIds: string[];
  }) => void;
};

/** ArticleNav ノードかどうか */
const isArticleNavNode = (node: KnzNode): boolean => node.name === 'ArticleNav';

/** ノード配列を描画する（再帰） */
const renderNodes = (nodes: KnzNode[]): React.ReactNode =>
  nodes.map((node, i) => {
    const def = getComponentDef(node.name);
    if (!def) return null; // 未登録は描画しない（安全側）
    const Comp = def.component;
    return (
      <Comp
        key={node.id ?? `${node.name}-${i}`}
        node={node}
        renderChildren={renderNodes}
        renderInline={(text) => <InlineText text={text} />}
      />
    );
  });

/**
 * knz ソースをパースして描画するビューワー。
 *
 * エラー時の挙動（重大度で切り分け）:
 * - Error が 1 件でもある場合: 本文を描画せずエラー画面を表示。
 * - Warning のみ: 該当箇所を無視して全体を描画。
 */
export const KnzViewer: React.FC<Props> = ({
  source,
  createdAt,
  updatedAt,
  hideHeader = false,
  fetchFile,
  tocMode = 'inline',
  onTocChange,
  articleNavContext = 'preview',
  onArticleNavChange,
}) => {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;
  const isDark = resolved === 'dark';

  const result = React.useMemo(() => parseKnz(source, registryLookup), [source]);

  // hoist モード時、Toc ブロックがあれば Section を収集して親へ通知する。
  // Toc が無い / パース失敗時は空配列を通知（親は右カラムを出さない）。
  const tocItems = React.useMemo<KnzTocItem[]>(() => {
    if (tocMode !== 'hoist' || !result.isValid) return [];
    if (!hasTocBlock(result.roots)) return [];
    return collectTocItems(result.roots);
  }, [tocMode, result.isValid, result.roots]);

  React.useEffect(() => {
    if (tocMode === 'hoist') onTocChange?.(tocItems);
  }, [tocMode, tocItems, onTocChange]);

  // ArticleNav（記事ナビ）ノードの有無。パース失敗時は描画されないため無しとみなす。
  // 案X: 本文フローからは除外し、記事の先頭（top）と末尾（bottom）に固定描画する。
  const articleNavNode =
    result.isValid && result.frontmatter ? result.roots.find(isArticleNavNode) : undefined;
  const hasArticleNav = !!articleNavNode;

  // ArticleNav が実際に表示する articleId（displayedIds）と、関連指定から溢れた articleId（overflowIds）。
  // displayedIds はおすすめ記事の重複除外に、overflowIds はおすすめ記事の先頭表示に使う。
  const { displayedIds, overflowIds } = React.useMemo<{
    displayedIds: string[];
    overflowIds: string[];
  }>(() => {
    if (!articleNavNode) return { displayedIds: [], overflowIds: [] };
    const self = typeof articleNavNode.params.self === 'string' ? articleNavNode.params.self : '';
    const articlesParam = articleNavNode.params.articles;
    const articles = Array.isArray(articlesParam)
      ? articlesParam.filter((a): a is string => typeof a === 'string' && !!a)
      : typeof articlesParam === 'string' && articlesParam
        ? [articlesParam]
        : [];
    return deriveArticleNavIds(self, articles);
  }, [articleNavNode]);

  // 下部スロットの実高さ（ArticleNav が ResizeObserver で測って通知する）。
  const [articleNavBottomHeight, setArticleNavBottomHeight] = React.useState(0);
  // 親へ「ArticleNav の有無 + 下部スロット高さ + 表示 ID / 溢れ ID」を通知する。
  // ID 配列は参照の揺れを避けるため join した文字列を依存にする。
  const displayedIdsKey = displayedIds.join(',');
  const overflowIdsKey = overflowIds.join(',');
  React.useEffect(() => {
    onArticleNavChange?.({
      present: hasArticleNav,
      bottomHeight: hasArticleNav ? articleNavBottomHeight : 0,
      displayedIds: hasArticleNav ? displayedIds : [],
      overflowIds: hasArticleNav ? overflowIds : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onArticleNavChange, hasArticleNav, articleNavBottomHeight, displayedIdsKey, overflowIdsKey]);

  // Error があれば描画しない
  if (!result.isValid || !result.frontmatter) {
    const errors = result.diagnostics.filter((d) => d.severity === 'error');
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ border: 1, borderColor: 'error.main', borderRadius: 2, p: 2.5, maxWidth: 720 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ErrorOutlineIcon sx={{ color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              この記事には構文エラーがあり表示できません
            </Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {errors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                frontmatter（title / author）が不足しています。
              </Typography>
            ) : (
              errors.map((e, i) => (
                <Typography key={i} variant="body2" color="text.secondary">
                  行 {e.line}: {e.message}
                </Typography>
              ))
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  // frontmatter に `prose: true` がある場合は読み物モード。
  // タイトル・副題・パンくずは frontmatter から取り、本文を紙面ラッパ（ProseArticle）で包む。
  // タイトルは紙面が担当するため frontmatter ヘッダー（KnzArticleHeader）は描画しない。
  const isProseArticle = result.frontmatter.extra.prose === 'true';

  // ArticleNav（記事ナビ）は本文中に 1 箇所書かれるが、描画は記事の上部・下部に振り分ける（案X）。
  // 本文フローからは除外し、先頭（slot=top）と末尾（slot=bottom）で別途描画する。
  // articleNavNode / hasArticleNav は上部で算出済み。
  const contentRoots = articleNavNode
    ? result.roots.filter((n) => !isArticleNavNode(n))
    : result.roots;

  /** ArticleNav を指定スロットで描画する（存在時のみ）。下部スロットは実高さを親へ通知する。 */
  const renderArticleNav = (slot: 'top' | 'bottom'): React.ReactNode => {
    if (!articleNavNode) return null;
    return (
      <ArticleNavSlotProvider
        slot={slot}
        navContext={articleNavContext}
        reportBottomHeight={slot === 'bottom' ? setArticleNavBottomHeight : undefined}
      >
        {renderNodes([articleNavNode])}
      </ArticleNavSlotProvider>
    );
  };

  if (isProseArticle) {
    return (
      <KnzFileResolverProvider resolver={fetchFile ?? null}>
        <KnzDocumentProvider roots={result.roots} tocMode={tocMode} navContext={articleNavContext}>
          <Box data-color-mode={isDark ? 'dark' : 'light'} sx={{ p: 2 }}>
            <ProseArticle
              title={result.frontmatter.title}
              subtitle={result.frontmatter.extra.subtitle}
              breadcrumb={result.frontmatter.extra.breadcrumb}
            >
              {renderNodes(result.roots)}
            </ProseArticle>
          </Box>
        </KnzDocumentProvider>
      </KnzFileResolverProvider>
    );
  }

  return (
    <KnzFileResolverProvider resolver={fetchFile ?? null}>
      <KnzDocumentProvider roots={result.roots} tocMode={tocMode} navContext={articleNavContext}>
        <Box data-color-mode={isDark ? 'dark' : 'light'} sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
          {!hideHeader && (
            <KnzArticleHeader
              frontmatter={result.frontmatter}
              createdAt={createdAt}
              updatedAt={updatedAt}
            />
          )}
          {articleNavNode && <Box sx={{ mt: hideHeader ? 0 : 2 }}>{renderArticleNav('top')}</Box>}
          <Stack spacing={2} sx={{ mt: hideHeader && !articleNavNode ? 0 : 2 }}>
            {renderNodes(contentRoots)}
          </Stack>
          {articleNavNode && <Box sx={{ mt: 3 }}>{renderArticleNav('bottom')}</Box>}
        </Box>
      </KnzDocumentProvider>
    </KnzFileResolverProvider>
  );
};

export default KnzViewer;
